/**
 * Barebone Header Component - JavaScript
 *
 * WCAG 2.1 AA compliant navigation interactions
 * Features: Keyboard navigation, screen reader support, focus management
 *
 * @package WordPress
 * @subpackage Header_Component
 * @version 1.0.0
 */

(function () {
  "use strict";

  // Configuration object
  const config = {
    selectors: {
      header: ".site-header",
      mobileToggle: ".site-header__toggle",
      mobileMenu: ".site-header__nav--mobile",
      desktopMenu: ".site-header__nav--primary",
      dropdownToggles: ".dropdown-toggle",
      submenus: ".sub-menu",
      hasDropdown: ".has-dropdown",
    },
    classes: {
      mobileMenuOpen: "mobile-menu-open",
      expanded: "expanded",
    },
    keys: {
      ENTER: "Enter",
      SPACE: " ",
      ESCAPE: "Escape",
      ARROW_DOWN: "ArrowDown",
      ARROW_UP: "ArrowUp",
      ARROW_LEFT: "ArrowLeft",
      ARROW_RIGHT: "ArrowRight",
      TAB: "Tab",
    },
    breakpoints: {
      mobile: 768,
    },
  };

  // State management
  const state = {
    mobileMenuOpen: false,
    openDropdowns: new Set(),
    lastFocusedElement: null,
    isDesktop: window.innerWidth >= config.breakpoints.mobile,
  };

  // Utility functions
  const utils = {
    /**
     * Debounce function for performance optimization
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    /**
     * Check if element is focusable
     */
    isFocusable(element) {
      return element.tabIndex >= 0 && !element.disabled && !element.hidden && element.offsetParent !== null;
    },

    /**
     * Get all focusable elements within a container
     */
    getFocusableElements(container) {
      const selectors = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ];

      return container.querySelectorAll(selectors.join(","));
    },

    /**
     * Trap focus within a container
     */
    trapFocus(container, event) {
      const focusableElements = this.getFocusableElements(container);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.key === config.keys.TAB) {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    },

    /**
     * Announce to screen readers
     */
    announce(message, priority = "polite") {
      const announcer = document.createElement("div");
      announcer.setAttribute("aria-live", priority);
      announcer.setAttribute("aria-atomic", "true");
      announcer.className = "screen-reader-text";
      announcer.textContent = message;

      document.body.appendChild(announcer);

      setTimeout(() => {
        document.body.removeChild(announcer);
      }, 1000);
    },
  };

  // Mobile menu functionality
  const mobileMenu = {
    toggle: null,
    menu: null,

    init() {
      this.toggle = document.querySelector(config.selectors.mobileToggle);
      this.menu = document.querySelector(config.selectors.mobileMenu);

      if (!this.toggle || !this.menu) return;

      this.bindEvents();
    },

    bindEvents() {
      // Toggle button click
      this.toggle.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleMenu();
      });

      // Close on escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === config.keys.ESCAPE && state.mobileMenuOpen) {
          this.close();
        }
      });

      // Focus trap
      this.menu.addEventListener("keydown", (e) => {
        if (state.mobileMenuOpen) {
          utils.trapFocus(this.menu, e);
        }
      });

      // Close on outside click
      document.addEventListener("click", (e) => {
        if (state.mobileMenuOpen && !this.menu.contains(e.target) && !this.toggle.contains(e.target)) {
          this.close();
        }
      });
    },

    toggleMenu() {
      if (state.mobileMenuOpen) {
        this.close();
      } else {
        this.open();
      }
    },

    open() {
      state.mobileMenuOpen = true;
      state.lastFocusedElement = document.activeElement;

      // Update ARIA attributes
      this.toggle.setAttribute("aria-expanded", "true");
      this.menu.setAttribute("aria-hidden", "false");

      // Add body class to prevent scrolling
      document.body.classList.add(config.classes.mobileMenuOpen);

      // Focus first focusable element in menu
      const firstFocusable = utils.getFocusableElements(this.menu)[0];
      if (firstFocusable) {
        firstFocusable.focus();
      }

      // Announce to screen readers
      utils.announce(headerData?.menuText || "Menu opened");
    },

    close() {
      state.mobileMenuOpen = false;

      // Update ARIA attributes
      this.toggle.setAttribute("aria-expanded", "false");
      this.menu.setAttribute("aria-hidden", "true");

      // Remove body class
      document.body.classList.remove(config.classes.mobileMenuOpen);

      // Close all mobile submenus
      this.closeAllSubmenus();

      // Return focus to toggle button
      if (state.lastFocusedElement) {
        state.lastFocusedElement.focus();
        state.lastFocusedElement = null;
      }

      // Announce to screen readers
      utils.announce("Menu closed");
    },

    closeAllSubmenus() {
      const submenus = this.menu.querySelectorAll(config.selectors.submenus);
      const dropdownToggles = this.menu.querySelectorAll(config.selectors.dropdownToggles);

      submenus.forEach((submenu) => {
        submenu.setAttribute("aria-hidden", "true");
      });

      dropdownToggles.forEach((toggle) => {
        toggle.setAttribute("aria-expanded", "false");
        toggle.parentElement.classList.remove(config.classes.expanded);
      });
    },
  };

  // Desktop dropdown functionality
  const dropdownMenu = {
    dropdowns: [],

    init() {
      this.dropdowns = document.querySelectorAll(config.selectors.hasDropdown);
      if (this.dropdowns.length === 0) return;

      this.bindEvents();
    },

    bindEvents() {
      this.dropdowns.forEach((dropdown) => {
        const toggle = dropdown.querySelector(config.selectors.dropdownToggles);
        const submenu = dropdown.querySelector(config.selectors.submenus);

        if (!toggle || !submenu) return;

        // Click events
        toggle.addEventListener("click", (e) => {
          if (state.isDesktop) {
            e.preventDefault();
            this.toggleDropdown(dropdown);
          } else {
            e.preventDefault();
            this.toggleMobileSubmenu(dropdown);
          }
        });

        // Keyboard events
        toggle.addEventListener("keydown", (e) => {
          this.handleKeyboardNavigation(e, dropdown);
        });

        // Mouse events for desktop
        if (state.isDesktop) {
          dropdown.addEventListener("mouseenter", () => {
            this.openDropdown(dropdown);
          });

          dropdown.addEventListener("mouseleave", () => {
            this.closeDropdown(dropdown);
          });
        }

        // Focus events
        dropdown.addEventListener("focusin", () => {
          if (state.isDesktop) {
            this.openDropdown(dropdown);
          }
        });

        dropdown.addEventListener("focusout", (e) => {
          if (state.isDesktop && !dropdown.contains(e.relatedTarget)) {
            this.closeDropdown(dropdown);
          }
        });
      });

      // Close dropdowns when clicking outside
      document.addEventListener("click", (e) => {
        if (state.isDesktop) {
          this.dropdowns.forEach((dropdown) => {
            if (!dropdown.contains(e.target)) {
              this.closeDropdown(dropdown);
            }
          });
        }
      });

      // Close dropdowns on escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === config.keys.ESCAPE) {
          this.closeAllDropdowns();
        }
      });
    },

    toggleDropdown(dropdown) {
      const isOpen = state.openDropdowns.has(dropdown);
      if (isOpen) {
        this.closeDropdown(dropdown);
      } else {
        this.openDropdown(dropdown);
      }
    },

    openDropdown(dropdown) {
      const toggle = dropdown.querySelector(config.selectors.dropdownToggles);
      const submenu = dropdown.querySelector(config.selectors.submenus);

      if (!toggle || !submenu) return;

      // Close other dropdowns at the same level
      this.closeSiblingDropdowns(dropdown);

      state.openDropdowns.add(dropdown);
      dropdown.classList.add(config.classes.expanded);
      toggle.setAttribute("aria-expanded", "true");
      submenu.setAttribute("aria-hidden", "false");

      // Announce to screen readers
      const submenuText = headerData?.expandText || "Submenu expanded";
      utils.announce(submenuText);
    },

    closeDropdown(dropdown) {
      const toggle = dropdown.querySelector(config.selectors.dropdownToggles);
      const submenu = dropdown.querySelector(config.selectors.submenus);

      if (!toggle || !submenu) return;

      state.openDropdowns.delete(dropdown);
      dropdown.classList.remove(config.classes.expanded);
      toggle.setAttribute("aria-expanded", "false");
      submenu.setAttribute("aria-hidden", "true");

      // Close child dropdowns
      const childDropdowns = submenu.querySelectorAll(config.selectors.hasDropdown);
      childDropdowns.forEach((child) => this.closeDropdown(child));
    },

    closeAllDropdowns() {
      this.dropdowns.forEach((dropdown) => {
        this.closeDropdown(dropdown);
      });
    },

    closeSiblingDropdowns(currentDropdown) {
      const parent = currentDropdown.parentElement;
      const siblings = parent.querySelectorAll(":scope > " + config.selectors.hasDropdown);

      siblings.forEach((sibling) => {
        if (sibling !== currentDropdown) {
          this.closeDropdown(sibling);
        }
      });
    },

    toggleMobileSubmenu(dropdown) {
      const toggle = dropdown.querySelector(config.selectors.dropdownToggles);
      const submenu = dropdown.querySelector(config.selectors.submenus);

      if (!toggle || !submenu) return;

      const isExpanded = toggle.getAttribute("aria-expanded") === "true";

      toggle.setAttribute("aria-expanded", !isExpanded);
      submenu.setAttribute("aria-hidden", isExpanded);
      dropdown.classList.toggle(config.classes.expanded, !isExpanded);
    },

    handleKeyboardNavigation(event, dropdown) {
      const toggle = dropdown.querySelector(config.selectors.dropdownToggles);
      const submenu = dropdown.querySelector(config.selectors.submenus);

      switch (event.key) {
        case config.keys.ENTER:
        case config.keys.SPACE:
          event.preventDefault();
          if (state.isDesktop) {
            this.toggleDropdown(dropdown);
          } else {
            this.toggleMobileSubmenu(dropdown);
          }
          break;

        case config.keys.ARROW_DOWN:
          if (state.isDesktop) {
            event.preventDefault();
            this.openDropdown(dropdown);
            const firstLink = submenu.querySelector("a");
            if (firstLink) firstLink.focus();
          }
          break;

        case config.keys.ARROW_UP:
          if (state.isDesktop) {
            event.preventDefault();
            this.openDropdown(dropdown);
            const links = submenu.querySelectorAll("a");
            const lastLink = links[links.length - 1];
            if (lastLink) lastLink.focus();
          }
          break;

        case config.keys.ESCAPE:
          if (state.isDesktop) {
            this.closeDropdown(dropdown);
            toggle.focus();
          }
          break;
      }
    },
  };

  // Responsive behavior
  const responsive = {
    init() {
      this.bindEvents();
      this.handleResize();
    },

    bindEvents() {
      window.addEventListener(
        "resize",
        utils.debounce(() => {
          this.handleResize();
        }, 250)
      );
    },

    handleResize() {
      const wasDesktop = state.isDesktop;
      state.isDesktop = window.innerWidth >= config.breakpoints.mobile;

      // If switching from mobile to desktop
      if (!wasDesktop && state.isDesktop) {
        // Close mobile menu
        if (state.mobileMenuOpen) {
          mobileMenu.close();
        }

        // Reinitialize dropdown behavior
        dropdownMenu.closeAllDropdowns();
      }

      // If switching from desktop to mobile
      if (wasDesktop && !state.isDesktop) {
        // Close all desktop dropdowns
        dropdownMenu.closeAllDropdowns();
      }
    },
  };

  // Initialization
  const init = () => {
    // Check if we're in the admin or login page
    if (document.body.classList.contains("wp-admin") || document.body.classList.contains("login")) {
      return;
    }

    mobileMenu.init();
    dropdownMenu.init();
    responsive.init();

    // Add loaded class for CSS animations
    const header = document.querySelector(config.selectors.header);
    if (header) {
      header.classList.add("header-loaded");
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Export for debugging (only in development)
  if (window.location.hostname === "localhost" || window.location.hostname.includes(".local")) {
    window.headerNavigation = {
      state,
      mobileMenu,
      dropdownMenu,
      utils,
    };
  }
})();
